import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface TechTip {
  id: string;
  title: string;
  content: string;
  category: string;
  image_url?: string;
  video_url?: string;
  is_featured: boolean;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  rating: number;
  rating_count: number;
  view_count: number;
}

export const useTechTips = () => {
  return useQuery({
    queryKey: ['tech-tips'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tech_tips')
        .select('*')
        .eq('is_published', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as TechTip[];
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 15 * 60 * 1000, // Cache for 15 minutes
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
  });
};

export const useTodaysTechTip = () => {
  return useQuery({
    queryKey: ['todays-tech-tip'],
    queryFn: async () => {
      console.log('Fetching today\'s tech tip from database...');
      const { data, error } = await supabase
        .from('tech_tips')
        .select('*')
        .eq('is_published', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      console.log('Today\'s tech tip data:', data);
      return data as TechTip | null;
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 15 * 60 * 1000, // Cache for 15 minutes
  });
};

export const useCreateTechTip = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (techTip: Omit<TechTip, 'id' | 'created_at' | 'updated_at' | 'rating' | 'rating_count' | 'view_count'>) => {
      const { data, error } = await supabase
        .from('tech_tips')
        .insert([techTip])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tech-tips'] });
      queryClient.invalidateQueries({ queryKey: ['todays-tech-tip'] });
      toast({
        title: "Success",
        description: "Tech tip created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create tech tip",
        variant: "destructive",
      });
      console.error('Error creating tech tip:', error);
    },
  });
};

export const useUpdateTechTip = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<TechTip> }) => {
      const { data, error } = await supabase
        .from('tech_tips')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tech-tips'] });
      queryClient.invalidateQueries({ queryKey: ['todays-tech-tip'] });
      toast({
        title: "Success",
        description: "Tech tip updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update tech tip",
        variant: "destructive",
      });
      console.error('Error updating tech tip:', error);
    },
  });
};

export const useDeleteTechTip = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('tech_tips')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tech-tips'] });
      queryClient.invalidateQueries({ queryKey: ['todays-tech-tip'] });
      toast({
        title: "Success",
        description: "Tech tip deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete tech tip",
        variant: "destructive",
      });
      console.error('Error deleting tech tip:', error);
    },
  });
};